/** XeAgents.ai — Landing page live voice demo widget (Twilio Client SDK) **/
import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PhoneCall, PhoneOff, Mic, MicOff, Bot, X, AlertCircle, RefreshCw } from "lucide-react";
import { Device, Call } from "@twilio/voice-sdk";
import { openBookingPage } from "./CalEmbed";

/* ─────────── API Configuration ─────────── */
// Get API base URL from environment variable, fall back to window.location.origin
const API_BASE = import.meta.env.VITE_API_BASE || window.location.origin;

/* ─────────── CSRF Token Helper ─────────── */
const CSRF_COOKIE_NAME = "XSRF-TOKEN";
const CSRF_HEADER_NAME = "X-XSRF-TOKEN";

/**
 * Get CSRF token from cookie for state-changing requests
 */
function getCsrfToken(): string | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/* ─────────── Types ─────────── */
type CallState = "idle" | "connecting" | "active" | "ended";

/* No call duration limit — users can talk as long as they want */

/* ─────────── Sound Wave Bars ─────────── */
const SoundWaveBars = ({ active, barCount = 6 }: { active: boolean; barCount?: number }) => (
  <div className="flex items-end gap-[3px] h-8">
    {Array.from({ length: barCount }).map((_, i) => (
      <motion.div
        key={i}
        className="w-[4px] rounded-full bg-gradient-to-t from-blue-600 to-indigo-400"
        animate={
          active
            ? { height: [6, 20 + (i % 3) * 6, 4, 24 + (i % 2) * 4, 8] }
            : { height: 4 }
        }
        transition={
          active
            ? { duration: 0.7 + i * 0.1, repeat: Infinity, ease: "easeInOut", delay: i * 0.07 }
            : { duration: 0.3 }
        }
      />
    ))}
  </div>
);

/* ─────────── Pulse Ring ─────────── */
const PulseRing = ({ delay, size }: { delay: number; size: string }) => (
  <motion.div
    className={`absolute inset-0 m-auto rounded-full border-2 border-blue-400/40 ${size}`}
    animate={{ scale: [1, 1.7, 2.2], opacity: [0.5, 0.15, 0] }}
    transition={{ duration: 2.2, repeat: Infinity, delay, ease: "easeOut" }}
  />
);

/* ─────────── Elapsed Time Display ─────────── */
const ElapsedTime = ({ elapsed }: { elapsed: number }) => {
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;

  return (
    <div className="relative flex items-center justify-center w-16 h-16">
      <svg className="absolute inset-0 -rotate-90" width="64" height="64">
        <circle cx="32" cy="32" r={28} fill="none" stroke="#e2e8f0" strokeWidth="4" />
        <circle
          cx="32" cy="32" r={28} fill="none"
          stroke="#3b82f6"
          strokeWidth="4" strokeLinecap="round"
          strokeDasharray={2 * Math.PI * 28} strokeDashoffset={0}
        />
      </svg>
      <span className="text-sm font-bold tabular-nums text-slate-700">
        {mins}:{secs.toString().padStart(2, "0")}
      </span>
    </div>
  );
};

/* ─────────── Call Ended Prompt ─────────── */
const CallEndedPrompt = ({
  onClose,
  errorMsg,
  onRetry,
  onScheduleDemo,
}: {
  onClose: () => void;
  errorMsg: string | null;
  onRetry: () => void;
  onScheduleDemo: () => void;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      className="flex flex-col items-center gap-5 text-center px-2"
    >
      {errorMsg ? (
        <>
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <div>
            <p className="text-base font-semibold text-slate-800">Connection failed</p>
            <p className="text-xs text-slate-500 mt-1 max-w-xs">{errorMsg}</p>
          </div>
          <div className="flex flex-col gap-2 w-full">
            <button
              onClick={onRetry}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-sm shadow-lg shadow-blue-500/30 hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
            <button
              onClick={() => {
                onClose();
                setTimeout(() => {
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                }, 150);
              }}
              className="w-full py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium text-sm hover:bg-slate-50 transition-colors"
            >
              Contact Us
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
            <PhoneCall className="w-7 h-7 text-green-600" />
          </div>
          <div>
            <p className="text-base font-semibold text-slate-800">Call Ended</p>
            <p className="text-xs text-slate-500 mt-1 max-w-xs">
              Thanks for experiencing our AI agent! Want to see how it can transform your business? Schedule a personalized demo to explore all features and capabilities.
            </p>
          </div>
          <div className="flex flex-col gap-2 w-full">
            <button
              onClick={onScheduleDemo}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-sm shadow-lg shadow-blue-500/30 hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              <PhoneCall className="w-4 h-4" />
              Schedule Demo
            </button>
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium text-sm hover:bg-slate-50 transition-colors"
            >
              Close
            </button>
          </div>
        </>
      )}
    </motion.div>
  );
};

/* ─────────────────────────────────────────
   MAIN COMPONENT — Twilio Client SDK
   ───────────────────────────────────────── */
export function VoiceCallWidget() {
  const [callState, _setCallState] = useState<CallState>("idle");
  const [isMuted, setIsMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [agentSpeaking] = useState(false);
  const [statusText, setStatusText] = useState("Connecting…");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ── callStateRef: always in sync with callState, safe to use in async callbacks ──
  const callStateRef = useRef<CallState>("idle");
  const deviceRef = useRef<Device | null>(null);
  const activeCallRef = useRef<Call | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Wrapper that keeps the ref in sync
  const setCallState = useCallback((s: CallState) => {
    callStateRef.current = s;
    _setCallState(s);
  }, []);

  /* ── cleanup (does NOT change callState) ── */
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (activeCallRef.current) {
      try {
        activeCallRef.current.disconnect();
      } catch {
        // Already disconnected
      }
      activeCallRef.current = null;
    }
    if (deviceRef.current) {
      try {
        deviceRef.current.destroy();
      } catch {
        // Already destroyed
      }
      deviceRef.current = null;
    }
  }, []);

  /* ── end the call → shows "ended" state with HumanRedirectPrompt ── */
  const endCall = useCallback(() => {
    cleanup();
    setCallState("ended");
  }, [cleanup, setCallState]);

  /* ── start the call via Twilio Client SDK ── */
  const startCall = useCallback(async () => {
    // Reset state
    setCallState("connecting");
    setStatusText("Connecting…");
    setErrorMsg(null);
    setElapsed(0);
    setIsMuted(false);

    try {
      // 1. Get Twilio access token from server
      const csrfToken = getCsrfToken();
      const tokenResp = await fetch(`${API_BASE}/api/public/twilio-voice-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken && { [CSRF_HEADER_NAME]: csrfToken }),
          // Required for ngrok free tier to bypass interstitial page
          "ngrok-skip-browser-warning": "true",
        },
        credentials: "include",
      });

      if (!tokenResp.ok) {
        const body = await tokenResp.json().catch(() => ({}));
        throw new Error(body.message || "Could not get voice token. Please check Twilio configuration.");
      }

      const { token } = await tokenResp.json();

      if (!token) {
        throw new Error("No voice token received from server.");
      }

      console.log("✅ [VoiceWidget] Got Twilio access token");

      // 2. Initialize Twilio Device with optimized codec settings
      // CRITICAL FIX FOR AUDIO DISTORTION:
      // Use PCMU (G.711) first instead of Opus for better phone network compatibility
      // PCMU is the standard telephony codec used in PSTN networks - no transcoding needed
      // Opus is great for WebRTC but causes distortion when bridging to traditional phone lines
      const device = new Device(token, {
        codecPreferences: [Call.Codec.PCMU, Call.Codec.Opus],
        logLevel: 1,
        maxAverageBitrate: 32000,
      });

      deviceRef.current = device;

      // IMPORTANT: Set up ALL event handlers BEFORE calling register()
      // Handle device errors
      device.on("error", (error: any) => {
        console.error("❌ [VoiceWidget] Device error:", error);
        if (callStateRef.current !== "idle" && callStateRef.current !== "ended") {
          setErrorMsg(`Call error: ${error.message || "Unknown error"}`);
          endCall();
        }
      });

      // Handle device registered (WebSocket connection established)
      device.on("registered", () => {
        console.log("✅ [VoiceWidget] Device registered successfully - WebSocket connected");
      });

      // Handle token expiry
      device.on("tokenWillExpire", async () => {
        console.log("⚠️ [VoiceWidget] Token expiring, refreshing...");
        try {
          const refreshCsrfToken = getCsrfToken();
          const refreshResp = await fetch(`${API_BASE}/api/public/twilio-voice-token`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(refreshCsrfToken && { [CSRF_HEADER_NAME]: refreshCsrfToken }),
              "ngrok-skip-browser-warning": "true",
            },
            credentials: "include",
          });
          const refreshData = await refreshResp.json();
          if (refreshData.token) {
            device.updateToken(refreshData.token);
            console.log("✅ [VoiceWidget] Token refreshed");
          }
        } catch (err) {
          console.error("❌ [VoiceWidget] Token refresh failed:", err);
        }
      });

      // CRITICAL: Register the device to establish WebSocket connection
      // This MUST be called before attempting to connect
      console.log("📡 [VoiceWidget] Registering device to establish WebSocket connection...");
      try {
        await device.register();
        console.log("✅ [VoiceWidget] Device registered, ready to make calls");
      } catch (registerErr: any) {
        console.error("❌ [VoiceWidget] Device registration failed:", registerErr);
        throw new Error(`Device registration failed: ${registerErr.message || "Unknown error"}`);
      }

      // 3. Fetch the Twilio Phone Number from backend (dynamic, not hardcoded)
      console.log(`📞 [VoiceWidget] Fetching agent phone number from backend...`);
      let agentPhoneToCall = null;
      
      try {
        // Geo-routing (India vs international). The IP lookup this used (ip-api.com)
        // answers 403 over HTTPS, so infer India from the browser's time zone instead.
        // Anyone it misses simply gets the default number.
        let visitorCountry = '';
        try {
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
          if (timeZone === 'Asia/Kolkata' || timeZone === 'Asia/Calcutta') visitorCountry = 'IN';
        } catch {
          // Time zone unavailable - the default number is used.
        }

        const phoneResp = await fetch(`${API_BASE}/api/public/live-agent-phone?country=${encodeURIComponent(visitorCountry)}`, {
          method: "GET",
          headers: {
            // Required for ngrok free tier to bypass interstitial page
            "ngrok-skip-browser-warning": "true",
          },
        });
        
        if (phoneResp.ok) {
          const phoneData = await phoneResp.json();
          agentPhoneToCall = phoneData.phoneNumber;
          console.log(`✅ [VoiceWidget] Fetched agent phone from backend: ${agentPhoneToCall}`);
        } else {
          console.warn(`⚠️ [VoiceWidget] Failed to fetch phone number, status: ${phoneResp.status}`);
        }
      } catch (phoneErr: any) {
        console.error(`❌ [VoiceWidget] Error fetching phone number:`, phoneErr.message);
      }
      
      if (!agentPhoneToCall) {
        throw new Error("Voice widget phone number not configured. Please configure it in Admin Settings → Master → Landing Page Voice Widget.");
      }

      // 4. Connect (place outgoing call) - ONLY after device is registered
      // Pass the phone number to Twilio (backend will use this or fall back to settings)
      console.log(`📞 [VoiceWidget] Placing call via Twilio Device...`);
      const connectParams: any = {};
      if (agentPhoneToCall) {
        connectParams.params = { To: agentPhoneToCall };
      }
      const call = await device.connect(connectParams);
      activeCallRef.current = call;

      // Call accepted (ringing → connected)
      call.on("accept", () => {
        console.log("✅ [VoiceWidget] Call accepted/connected");
        setCallState("active");
        setStatusText("Connected — Speaking with AI Agent…");

        // Start elapsed time counter
        timerRef.current = setInterval(() => {
          setElapsed((prev) => prev + 1);
        }, 1000);
      });

      // Call disconnected
      call.on("disconnect", () => {
        console.log("🔌 [VoiceWidget] Call disconnected");
        if (callStateRef.current === "active" || callStateRef.current === "connecting") {
          endCall();
        }
      });

      // Call cancelled (rejected before connecting)
      call.on("cancel", () => {
        console.log("❌ [VoiceWidget] Call cancelled");
        if (callStateRef.current !== "idle" && callStateRef.current !== "ended") {
          setErrorMsg("Call was cancelled before connecting.");
          endCall();
        }
      });

      // Call error
      call.on("error", (error: any) => {
        console.error("❌ [VoiceWidget] Call error:", error);
        if (callStateRef.current !== "idle" && callStateRef.current !== "ended") {
          setErrorMsg(`Call failed: ${error.message || "Connection error"}`);
          endCall();
        }
      });

      // Call rejected
      call.on("reject", () => {
        console.log("❌ [VoiceWidget] Call rejected");
        if (callStateRef.current !== "idle" && callStateRef.current !== "ended") {
          setErrorMsg("Call was rejected. The agent may be unavailable.");
          endCall();
        }
      });

    } catch (err: any) {
      console.error("❌ [VoiceWidget] Start call error:", err);
      setErrorMsg(err.message || "Could not start the call.");
      cleanup();
      setCallState("ended");
    }
  }, [setCallState, endCall, cleanup]);

  /* ── toggle mute ── */
  const toggleMute = useCallback(() => {
    const call = activeCallRef.current;
    if (call) {
      if (isMuted) {
        call.mute(false);
      } else {
        call.mute(true);
      }
    }
    setIsMuted((m) => !m);
  }, [isMuted]);

  /* ── explicitly close the modal (user presses X or "No, Thanks") ── */
  const closeModal = useCallback(() => {
    cleanup();
    setCallState("idle");
    setErrorMsg(null);
    setElapsed(0);
    setIsMuted(false);
  }, [cleanup, setCallState]);

  useEffect(() => () => { cleanup(); }, [cleanup]);

  // Listen for popup trigger
  useEffect(() => {
    const handleOpen = () => {
      if (callStateRef.current === "idle") {
        startCall();
      }
    };
    window.addEventListener("open-live-call", handleOpen as EventListener);
    return () => {
      window.removeEventListener("open-live-call", handleOpen as EventListener);
    };
  }, [startCall]);

  const isOpen = callState !== "idle";

  return (
    <>
      {/* ── Super Attractive Floating Call CTA ── */}
      {callState === "idle" && (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 flex flex-col items-end sm:flex-row sm:items-center gap-2 sm:gap-4 w-auto max-w-full overflow-hidden">
          
          {/* Animated Text Badge */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="flex flex-col items-end"
          >
            <motion.span
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="bg-gradient-to-r from-pink-500 via-red-500 to-orange-500 text-white text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-1.5 rounded-full shadow-lg shadow-pink-500/30 whitespace-nowrap"
            >
              🎧 Try Calling AI Agent Live
            </motion.span>
            <motion.span
              className="text-[10px] text-slate-500 mt-1 pr-1"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2.5, repeat: Infinity }}
            >
              Talk to our AI instantly
            </motion.span>
          </motion.div>

          {/* Glowing Button */}
          <motion.button
            onClick={startCall}
            whileHover={{ scale: 1.12 }}
            whileTap={{ scale: 0.9 }}
            className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-orange-500 shadow-2xl shadow-pink-500/40 flex items-center justify-center text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-pink-400"
            aria-label="Test Live AI Call"
            title="Test Live AI Call"
          >
            {/* Outer Glow Pulse */}
            <motion.span
              className="absolute inset-0 rounded-full border-2 border-pink-400/50"
              animate={{ scale: [1, 1.7], opacity: [0.6, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
            />
            <motion.span
              className="absolute inset-0 rounded-full border-2 border-orange-400/40"
              animate={{ scale: [1, 2], opacity: [0.5, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.6, ease: "easeOut" }}
            />

            <PhoneCall className="w-7 h-7 relative z-10" strokeWidth={2.5} />
          </motion.button>
        </div>
      )}

      {/* ── Modal overlay ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="call-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: "spring", stiffness: 280, damping: 24 }}
              className="relative w-full max-w-md md:max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              {/* Close button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors z-10"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>

              {/* ── Connecting / Active call UI ── */}
              {(callState === "connecting" || callState === "active") && (
                <div className="flex flex-col items-center gap-5 px-6 pt-8 pb-8">
                  {/* Avatar */}
                  <div className="relative flex items-center justify-center w-20 h-20">
                    <PulseRing delay={0} size="w-20 h-20" />
                    <PulseRing delay={0.9} size="w-20 h-20" />
                    <motion.div
                      className="relative z-10 w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-xl shadow-blue-500/30"
                      animate={{ scale: agentSpeaking ? [1, 1.06, 1] : 1 }}
                      transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Bot className="w-8 h-8 text-white" />
                    </motion.div>
                  </div>

                  <div className="text-center">
                    <p className="font-semibold text-slate-800 text-base">VoicePro AI Agent</p>
                    <p className="text-xs text-slate-500 mt-0.5">{statusText}</p>
                  </div>

                  {callState === "active" && (
                    <SoundWaveBars active barCount={7} />
                  )}

                  {callState === "active" && (
                    <ElapsedTime elapsed={elapsed} />
                  )}

                  {callState === "connecting" && (
                    <div className="flex gap-1.5">
                      {[0, 0.2, 0.4].map((d) => (
                        <motion.div
                          key={d}
                          className="w-2 h-2 rounded-full bg-blue-500"
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 1, repeat: Infinity, delay: d }}
                        />
                      ))}
                    </div>
                  )}

                  {(callState === "active" || callState === "connecting") && (
                    <div className="flex items-center gap-4 mt-2">
                      {callState === "active" && (
                        <motion.button
                          onClick={toggleMute}
                          whileHover={{ scale: 1.08 }}
                          whileTap={{ scale: 0.93 }}
                          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                            isMuted
                              ? "bg-red-100 text-red-600"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                          aria-label={isMuted ? "Unmute" : "Mute"}
                        >
                          {isMuted ? (
                            <MicOff className="w-5 h-5" />
                          ) : (
                            <Mic className="w-5 h-5" />
                          )}
                        </motion.button>
                      )}

                      <motion.button
                        onClick={() => endCall()}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.92 }}
                        className="px-5 py-3 rounded-2xl bg-red-500 hover:bg-red-600 flex items-center gap-2 text-white font-semibold shadow-lg shadow-red-500/40 transition-colors"
                        aria-label="Disconnect call"
                      >
                        <PhoneOff className="w-5 h-5" />
                        Disconnect
                      </motion.button>
                    </div>
                  )}

                  <p className="text-[10px] text-slate-400 text-center">
                    Calls go through your configured incoming agent.
                  </p>
                </div>
              )}

              {/* ── Ended state: show CallEndedPrompt ── */}
              {callState === "ended" && (
                <div className="px-6 pt-8 pb-8">
                  <CallEndedPrompt
                    onClose={closeModal}
                    errorMsg={errorMsg}
                    onRetry={() => {
                      closeModal();
                      setTimeout(() => startCall(), 150);
                    }}
                    onScheduleDemo={() => {
                      closeModal();
                      setTimeout(openBookingPage, 150);
                    }}
                  />
                </div>
              )}

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
}

export default VoiceCallWidget;